import { LazyAction, LazyModule, LazyImport } from 'owa-bundling';

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "SearchService" */ './lazyIndex')
);

/**
 * Functions that are time sensitive (executeSearchService, substrateSearchService,
 * substrateSearchSuggestionsService) should never be lazily exported as they will
 * go through a Promise resolve which will delay the request from going out.
 */
export { default as executeSearchService } from './services/executeSearchService';
export { default as substrateSearchService } from './services/substrateSearchService';
export {
    default as substrateSearchSuggestionsService,
} from './services/substrateSearchGetSuggestionsService';
export {
    default as executeSearchStartSearchSessionService,
} from './services/executeSearchStartSearchSessionService';
export {
    default as executeSearchEndSearchSessionService,
} from './services/executeSearchEndSearchSessionService';

export { default as compareSearchScope } from './helpers/searchScope/compareSearchScope';
export {
    getKey,
    getSearchScopeList,
    getSearchScopeType,
} from './helpers/searchScope/SearchScenario';

export {
    SearchEntityActionsEventType,
    EntityActionTakenType,
} from './actions/substrateSearchLogEvents';

export let lazyLogResponseRendered = new LazyAction(lazyModule, m => m.logResponseRendered);
export let lazyLogEntityClicked = new LazyAction(lazyModule, m => m.logEntityClicked);
export let lazyLogSecondaryEntityActionClicked = new LazyAction(
    lazyModule,
    m => m.logSecondaryEntityActionClicked
);
export let lazyLogFeedback = new LazyAction(lazyModule, m => m.logFeedback);
export let lazyLogSearchDone = new LazyAction(lazyModule, m => m.logSearchDone);
export let lazyLogSessionEnd = new LazyAction(lazyModule, m => m.logSessionEnd);
export let lazyLogResponseReceived = new LazyAction(lazyModule, m => m.logResponseReceived);
export let lazyLogExecuteSearchCorrelation = new LazyAction(
    lazyModule,
    m => m.logExecuteSearchCorrelation
);

export let lazyLogLocalContentDataSource = new LazyAction(
    lazyModule,
    m => m.logLocalContentDataSource
);

export let lazyLogLocalContentLayout = new LazyAction(lazyModule, m => m.logLocalContentLayout);

export let lazyClearLocalContentInstrumentationCache = new LazyAction(
    lazyModule,
    m => m.clearLocalContentInstrumentationCache
);

export let lazyGetLocalContentId = new LazyImport(lazyModule, m => m.getLocalContentId);

export let lazyLogResponseNeverRendered = new LazyAction(
    lazyModule,
    m => m.logResponseNeverRendered
);

export let lazyLogSearchResultInteraction = new LazyAction(
    lazyModule,
    m => m.logSearchResultInteraction
);

export const lazyFlushNonRenderedItemsFromCache = new LazyAction(
    lazyModule,
    m => m.flushNonRenderedResponseReceivedItemsFromCache
);

export const lazySubstrateSearchInitOperation = new LazyImport(
    lazyModule,
    m => m.substrateSearchInitOperation
);

export const lazyLogSearchBoxStateChanged = new LazyAction(
    lazyModule,
    m => m.logSearchBoxStateChanged
);

export {
    SubstrateSearchEndpoint,
    default as getSubstrateSearchEndpoint,
} from './helpers/getSubstrateSearchEndpoint';

export { default as getSearchRestrictions } from './helpers/getSearchRestrictions';

export {
    primaryMailboxSearchScope,
    archiveMailboxSearchScope,
    sharedFoldersSearchScope,
    singleGroupSearchScope,
    calendarSearchScope,
    SearchScope,
    PrimaryMailboxSearchScope,
    ArchiveMailboxSearchScope,
    SharedFoldersSearchScope,
    SingleGroupSearchScope,
    CalendarSearchScope,
    SearchScopeKind,
} from './data/schema/SearchScope';
import {
    FileSuggestion,
    KeywordSuggestion,
    PeopleSuggestion,
    PrivateDistributionListSuggestion,
    MessageSuggestion,
    CategorySearchSuggestion,
    PillSuggestion,
    EventSuggestion,
    SuggestionKind,
    Suggestion,
    SuggestionSource,
} from './data/schema/SuggestionSet';
import {
    SubstrateSearchSuggestionsResponseTextSuggestion,
    SubstrateSearchSuggestionsResponsePeopleSuggestion,
    SubstrateSearchSuggestionsResponseMessageSuggestion,
    SubstrateSearchSuggestionsResponseAttachmentSuggestion,
    SubstrateSearchSuggestionsResponseEventSuggestion,
    default as SubstrateSearchSuggestionsResponse,
} from './data/schema/SubstrateSearchSuggestionsResponse';

import {
    LocalContentInstrumentationContext,
    LocalContentLayout,
    LocalContentProvider,
    LocalContentType,
    LocalContentLayoutGroup,
} from './actions/substrateSearchLogLocalContentEvents';

import SearchRequestInstrumentation from './data/schema/SearchRequestInstrumentation';

export { getLocalTime } from './actions/substrateSearchLogEvents';
export { PageContext, getPageContext, getNextPageContext } from './helpers/pageContext';
export {
    FileSuggestion,
    KeywordSuggestion,
    PeopleSuggestion,
    PrivateDistributionListSuggestion,
    MessageSuggestion,
    CategorySearchSuggestion,
    EventSuggestion,
    PillSuggestion,
    SuggestionKind,
    Suggestion,
    SuggestionSource,
    SubstrateSearchSuggestionsResponse,
    SubstrateSearchSuggestionsResponseTextSuggestion,
    SubstrateSearchSuggestionsResponsePeopleSuggestion,
    SubstrateSearchSuggestionsResponseMessageSuggestion,
    SubstrateSearchSuggestionsResponseAttachmentSuggestion,
    SubstrateSearchSuggestionsResponseEventSuggestion,
    SearchRequestInstrumentation,
    LocalContentInstrumentationContext,
    LocalContentLayout,
    LocalContentProvider,
    LocalContentType,
    LocalContentLayoutGroup,
};
export {
    SubstrateSearchSuggestionSegment,
    SubstrateSearchSuggestionPersonSegment,
    SubstrateSearchSuggestionTextSegment,
} from './data/schema/SubstrateSearchSuggestionsResponse';
export { default as SuggestionSet } from './data/schema/SuggestionSet';
export {
    default as convertSearchResultEventToCalendarItemType,
} from './helpers/substrateTransformations/response/convertSearchResultEventToCalendarItemType';
export {
    default as convertSearchResultConversationToConversationType,
} from './helpers/substrateTransformations/response/convertSearchResultConversationToConversationType';
export {
    default as convertSearchResultMessageToMessageType,
} from './helpers/substrateTransformations/response/convertSearchResultMessageToMessageType';
export {
    default as createDateTimeRangeFilter,
} from './helpers/substrateTransformations/request/entityRequest/createDateTimeRangeFilter';
export {
    default as createFoldersFilter,
} from './helpers/substrateTransformations/request/entityRequest/createFoldersFilter';
export { default as FlaggedToken } from './data/schema/FlaggedToken';
export { default as ConnectedAccountInfo } from './data/schema/ConnectedAccountInfo';

export {
    default as SubstrateSearchRequest,
    AndFilter,
    EntityRequest,
    EntityRequestRefiner,
    EntityType,
    FilterBase,
    OrFilter,
    RangeFilter,
    RangeFilterExpression,
    ScenarioDimension,
    ScenarioType,
    SearchQuery,
    SearchSortOrder,
    SortableField,
    SortDirection,
    TermFilter,
    TermFilterExpression,
} from './data/schema/SubstrateSearchRequest';
export {
    default as SubstrateSearchResponse,
    EntitySet,
    EntitySetType,
    InstrumentationDetails,
    QueryAlterationResponse,
    QueryAlterationType,
    QueryId,
    SearchResult,
    SearchResultBusyType,
    SearchResultCalendarItemType,
    SearchResultConversation,
    SearchResultEmailAddress,
    SearchResultEvent,
    SearchResultExtendedPropertyType,
    SearchResultFlagStatus,
    SearchResultFlagType,
    SearchResultFolderId,
    SearchResultItem,
    SearchResultItemId,
    SearchResultMessage,
    SearchResultRecipient,
    SearchResultSet,
} from './data/schema/SubstrateSearchResponse';
export { Provenance } from './data/schema/SubstrateSearchShared';
export {
    default as SuperExecuteSearchResponseMessage,
} from './data/schema/SuperExecuteSearchResponseMessage';
export {
    default as getSearchResultSet,
} from './helpers/substrateTransformations/response/getSearchResultSet';
export { default as getTimeZone } from './helpers/substrateTransformations/request/getTimeZone';
export { buildInstrumentation } from './services/makeSearchRequest';
export { default as SubstrateSearchScenario } from './data/schema/SubstrateSearchScenario';

export const lazyDeleteSubstrateSearchHistoryService = new LazyImport(
    lazyModule,
    m => m.deleteSubstrateSearchHistoryService
);
export const lazyExportSubstrateSearchHistoryService = new LazyImport(
    lazyModule,
    m => m.exportSubstrateSearchHistoryService
);
